import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Card } from '../../components/ui/Card';
import { api } from '../../lib/api';
import { isRealtimeHealthy, onRealtimeHealthChange, subscribeToStation } from '../../lib/realtime';
import { BoostButton } from '../payments/BoostButton';
import { Artwork } from '../dashboard/Artwork';
import { TIER_TONE, startsIn } from '../rooms/api';
import { SwapDialog } from './SwapDialog';
import { useSession } from '../../stores/session';

/**
 * Where you are waiting, across every room.
 *
 * ## Why it belongs on a profile rather than in a room
 *
 * A submission is a thing you did somewhere else and then have to go back and
 * check. Until now the only way to find out where you stood was to remember
 * which rooms you had submitted to, open each one, and scan a strip of eight
 * cards for your own title. People do not do that; they submit and forget, and
 * a queue you forget about is a track that plays to nobody who cared.
 *
 * ## Under the badges, and only your own
 *
 * It is the same kind of thing as the shelf above it - a record of what you
 * have been doing - and it is the last such thing before the page turns into
 * music. Nobody else sees it: where somebody is waiting is not a fact about
 * them that a visitor is owed, and a public list of pending submissions would
 * be a queue-jumping map.
 *
 * ## The wait is the server's arithmetic
 *
 * Position and estimate both come from ordering each room the way the room
 * orders itself - see `MyQueueAction`. Nothing here recomputes a place in a
 * queue, because a place in a queue is not a column.
 */
interface Waiting {
  queue_id: number;
  position: number;
  starts_in_ms: number | null;
  boost_tier: 'free' | 'basic' | 'priority' | 'earned' | 'pick';
  track: { id: number; title: string; artwork_small: string | null };
  room: {
    id: number;
    username: string;
    name: string | null;
    host: { display_name: string };
    status: string;
    boosts: { basic_cents: number; priority_cents: number } | null;
  };
}

export function MyQueue() {
  const me = useSession((state) => state.user);
  const queryClient = useQueryClient();
  const [swapping, setSwapping] = useState<Waiting | null>(null);

  const withdraw = useMutation({
    mutationFn: (queueId: number) => api.delete(`/me/queue/${queueId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me', 'queue'] }),
  });

  /*
   * The socket's actual state, not merely whether one is configured. Same
   * reasoning as `useRoom`: a poll relaxed for events that are not arriving is
   * the worst of both.
   */
  const [healthy, setHealthy] = useState(isRealtimeHealthy);

  useEffect(() => onRealtimeHealthChange(() => setHealthy(isRealtimeHealthy())), []);

  const { data, dataUpdatedAt } = useQuery({
    queryKey: ['me', 'queue'],
    queryFn: () => api.get<{ items: Waiting[] }>('/me/queue'),
    /*
     * A backstop, not the mechanism.
     *
     * What actually moves this list is the rooms themselves - a track ending,
     * somebody boosting past you, a host reordering - and each of those pushes
     * an event this component subscribes to below. The interval is what catches
     * the rest: a socket that dropped, an event lost, a room that went quiet.
     *
     * Tighter when the socket is not carrying its share, because then it is the
     * only thing keeping the number true. People plan around this - the whole
     * point is knowing when to be in a room to hear their track - so being a
     * minute wrong is being wrong.
     */
    refetchInterval: healthy ? 20_000 : 5_000,
    refetchIntervalInBackground: false,
  });

  /*
   * The clock, locally.
   *
   * `starts_in_ms` was true when the response was built and has been falling
   * ever since. Refetching purely to redraw a number that decreases on its own
   * would be a request a second; instead the elapsed time since the fetch is
   * subtracted, and this tick only exists to make React redraw it.
   *
   * Ten seconds, because the estimate is quoted to the minute - anything
   * finer redraws for no visible change.
   */
  const [, tick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => tick((n) => n + 1), 10_000);

    return () => clearInterval(timer);
  }, []);

  /*
   * One subscription per room they are waiting in.
   *
   * `station.queue` covers submissions, boosts, removals and reorders;
   * `station.state` covers the track changing, which is what moves every
   * estimate at once. Both invalidate rather than merge - see the rule in
   * `realtime.ts`: an event says something changed, the client asks what.
   */
  const rooms = [...new Set((data?.items ?? []).map((item) => item.room.id))].sort().join(',');

  useEffect(() => {
    if (rooms === '') return;

    const stop = rooms.split(',').map((id) =>
      subscribeToStation(Number(id), () => {
        void queryClient.invalidateQueries({ queryKey: ['me', 'queue'] });
      }),
    );

    return () => stop.forEach((off) => off());
  }, [rooms, queryClient]);

  // Nothing waiting anywhere is the normal state, and it draws nothing.
  if (!data || data.items.length === 0) return null;

  return (
    <section aria-labelledby="my-queue" className="mt-8" data-my-queue>
      <h2 id="my-queue" className="meta-caps mb-3 text-faint">
        In the queue
      </h2>

      <div className="flex flex-col gap-2">
        {data.items.map((item) => {
          const live = item.room.status === 'live';

          return (
            <Card
              key={item.queue_id}
              className="flex flex-wrap items-center gap-4 p-3"
              data-waiting={item.queue_id}
            >
              <Artwork
                track={{
                  title: item.track.title,
                  artwork: null,
                  artwork_small: item.track.artwork_small,
                }}
                className="h-11 w-11 shrink-0 rounded-[var(--radius-chip)]"
              />

              <div className="min-w-0 flex-1">
                <Link
                  to={`/tracks/${item.track.id}`}
                  className="block truncate text-sm font-medium text-ink hover:underline"
                >
                  {item.track.title}
                </Link>
                {/*
                  Whose room, not whose track - you know what you submitted, and
                  the thing you cannot remember is where you sent it.
                */}
                <Link
                  to={`/${item.room.username}/queue`}
                  className="block truncate font-mono text-[11px] text-soft hover:underline"
                >
                  {item.room.name ?? item.room.host.display_name} · @{item.room.username}
                </Link>
              </div>

              <div className="flex shrink-0 items-center gap-4">
                <span className="flex flex-col items-center leading-tight">
                  <span className="text-sm font-bold text-fresh">#{item.position}</span>
                  <span className="meta-caps text-faint">queue</span>
                </span>

                <span className="flex flex-col items-center leading-tight">
                  <span className="font-mono text-[13px] text-ink" data-waiting-eta={item.queue_id}>
                    {live
                      ? item.starts_in_ms === null
                        ? 'paused'
                        : startsIn(Math.max(0, item.starts_in_ms - (Date.now() - dataUpdatedAt)))
                      : 'off air'}
                  </span>
                  <span className={`meta-caps ${TIER_TONE[item.boost_tier]}`}>
                    {item.boost_tier === 'pick' ? 'host pick' : item.boost_tier}
                  </span>
                </span>

                {/*
                  The one action worth having here.

                  This is exactly where somebody decides their track is not
                  coming up fast enough, and the server already refuses a boost
                  on anybody else's submission. Offered only when the room sells
                  them, so it is never a button that 403s.
                */}
                {item.room.boosts && (
                  <BoostButton
                    username={item.room.username}
                    queueId={item.queue_id}
                    tier={item.boost_tier}
                    prices={item.room.boosts}
                  />
                )}

                <button
                  type="button"
                  data-swap={item.queue_id}
                  onClick={() => setSwapping(item)}
                  className="meta-caps text-faint transition-colors hover:text-ink"
                >
                  Swap
                </button>

                {/*
                  Confirmed, and the confirmation says what it costs.

                  A free submission is a place in a queue and losing it is
                  nothing much. A paid boost is money, and the platform does not
                  refund one - the same as when a host drops it - so that has to
                  be said before the press rather than discovered after it.
                */}
                <button
                  type="button"
                  data-withdraw={item.queue_id}
                  disabled={withdraw.isPending}
                  onClick={() => {
                    const paid = item.boost_tier === 'basic' || item.boost_tier === 'priority';
                    const asked = paid
                      ? 'Take this back out? You paid to boost it, and withdrawing does not refund that.'
                      : 'Take this back out of the queue?';

                    if (window.confirm(asked)) withdraw.mutate(item.queue_id);
                  }}
                  className="meta-caps text-faint transition-colors hover:text-hot-text disabled:opacity-50"
                >
                  Withdraw
                </button>
              </div>
            </Card>
          );
        })}
      </div>

      {swapping && me && (
        <SwapDialog
          queueId={swapping.queue_id}
          username={me.username}
          currentTrackId={swapping.track.id}
          onClose={() => setSwapping(null)}
        />
      )}
    </section>
  );
}
